import React, { AnchorHTMLAttributes, FC } from 'react';
import {
  BodyProps,
  HeadingProps,
  LabelProps,
  SmallProps,
  SubtitleProps,
} from './TypographyNew.types';
import styled, { css } from 'styled-components';
import { TrevorIcon, TrevorIconName } from '../TrevorIcon';

const sharedHeaderStyles = css`
  margin: unset;
  font-family: 'Aeonik', sans-serif;
  color: var(--color-interface-content-normal);
  font-weight: bold;
`;

const sharedParagraphStyles = css<{ weight?: string }>`
  margin: unset;
  font-family: 'DM Sans', sans-serif;
  color: var(--color-interface-content-normal);
  font-weight: ${(p) => p.weight ?? 'regular'};

  &:disabled {
    color: red;
  }
`;

const headerVariants = {
  1: styled.h1`
    ${sharedHeaderStyles};
    font-size: 50px;
    line-height: 52px;
  `,
  2: styled.h2`
    ${sharedHeaderStyles};
    font-size: 32px;
    line-height: 32px;
  `,
  3: styled.h3`
    ${sharedHeaderStyles};
    font-size: 24px;
    line-height: 24px;
  `,
  4: styled.h4`
    ${sharedHeaderStyles};
    font-size: 20px;
    line-height: 20px;
  `,
};

const subtitleVariants = {
  1: styled.p`
    ${sharedParagraphStyles};
    font-size: 20px;
    line-height: 27px;
  `,
  2: styled.p`
    ${sharedParagraphStyles};
    font-size: 18px;
    line-height: 14px;
  `,
};

const bodyVariants = {
  1: styled.p`
    ${sharedParagraphStyles};
    font-size: 16px;
    line-height: 22px;
  `,
  2: styled.p`
    ${sharedParagraphStyles};
    font-size: 14px;
    line-height: 18px;
  `,
};

const labelVariants = {
  1: styled.p`
    ${sharedParagraphStyles};
    color: var(--color-interface-neutral-dark);
    font-size: 14px;
    line-height: 16px;
    font-weight: 500;
  `,
  2: styled.p`
    ${sharedParagraphStyles};
    color: var(--color-interface-neutral-dark);
    font-size: 12px;
    line-height: 16px;
    font-weight: 500;
  `,
};
export const Heading: FC<HeadingProps> = ({ variant, children, ...props }) => {
  const HeaderVariant = headerVariants[variant];
  return <HeaderVariant {...props}>{children}</HeaderVariant>;
};

export const Subtitle: FC<SubtitleProps> = ({
  variant,
  children,
  ...props
}) => {
  const SubtitleVariant = subtitleVariants[variant];
  return <SubtitleVariant {...props}>{children}</SubtitleVariant>;
};

export const Body: FC<BodyProps> = ({ variant, children, ...props }) => {
  const BodyVariant = bodyVariants[variant];
  return <BodyVariant {...props}>{children}</BodyVariant>;
};

export const Label: FC<LabelProps> = ({ variant, children, ...props }) => {
  const LabelVariant = labelVariants[variant];
  return <LabelVariant {...props}>{children}</LabelVariant>;
};
export const Small: FC<SmallProps> = styled.p`
  ${sharedParagraphStyles};
  margin: unset;
  font-size: 12px;
  line-height: 17px;
`;

// TODO: waiting for future designs for the links
export const LinkContainer = styled.a`
  margin: unset;
  font-family: 'DM Sans', sans-serif;
  color: var(--color-interface-secondary-normal);
  font-weight: 700;
  font-size: 12px;
  line-height: 16px;
  text-decoration: underline;
  gap: var(--spacing-s);
  display: flex;
  align-items: center;
  cursor: pointer;
  width: max-content;
`;

type LinkProps = React.AnchorHTMLAttributes<HTMLLinkElement> & {
  iconName?: TrevorIconName;
};

export const Link: FC<LinkProps> = ({ children, iconName, ...props }) => {
  return (
    <LinkContainer {...props}>
      {iconName && <TrevorIcon name={iconName} />}
      {children}
    </LinkContainer>
  );
};
